import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, PageContent } from "beproduct-ui";
import { Pencil } from "lucide-react";
import { PageHeader } from "@/components/navigation/page-header";
import jQuery from "jquery";
import "@progress/kendo-theme-default/dist/all.css";
import { TelerikReportViewer } from "@progress/telerik-react-report-viewer";
import { api } from "@/lib/api";

interface ReportRow {
  id: string;
  name: string;
  description: string | null;
}

// Telerik's HTML5 viewer was originally a jQuery widget and still reaches
// for `window.jQuery` / `window.$` at runtime. The React wrapper does
// not inject jQuery for us, so we do it once here before the component
// mounts.
if (typeof window !== "undefined") {
  const w = window as unknown as { jQuery?: unknown; $?: unknown };
  if (!w.jQuery) w.jQuery = jQuery;
  if (!w.$) w.$ = jQuery;
}

export const Route = createFileRoute("/_authed/$companySlug/reports/$reportId/")({
  component: ReportViewerPage,
});

function ReportViewerPage() {
  const { company, isAdmin } = Route.useRouteContext();
  const { companySlug, reportId } = Route.useParams();
  const nav = useNavigate({ from: "/$companySlug/reports/$reportId/" });

  // .NET Telerik REST lives at /api/reports under our service root. Through
  // Fastify that becomes /api/tenants/{id}/reports/api/reports. The
  // trailing slash matters — Telerik's client appends `clients`,
  // `clients/{id}/instances`, etc.
  const serviceUrl = `/api/tenants/${encodeURIComponent(company.id)}/reports/api/reports/`;

  // Pull the report's friendly name. We reuse the existing list query
  // — same queryKey as the Reports list page so it shares cache and
  // doesn't double-fetch when you navigate from list → viewer.
  const reportsQ = useQuery({
    queryKey: ["reports", company.id],
    queryFn: () =>
      api<ReportRow[]>(
        `/api/tenants/${encodeURIComponent(company.id)}/reports/reports`,
      ),
    staleTime: 60_000,
  });
  const reportName = reportsQ.data?.find((r) => r.id === reportId)?.name ?? null;

  // Pre-warm the image-proxy cache in the background. Telerik's PictureBox
  // fetches each image serially during data-binding, so a cold cache
  // means a multi-minute first open. The warm endpoint pulls every URL
  // through the proxy in parallel (16 at a time). Fire-and-forget here
  // so the viewer mounts immediately and the warm runs in parallel —
  // Telerik will hit warm cache for the images it gets to before
  // the warmer finishes, and pay the proxy round-trip otherwise.
  useEffect(() => {
    api(
      `/api/tenants/${encodeURIComponent(company.id)}/reports/reports/${reportId}/warm-images`,
      { method: "POST" },
    ).catch(() => {
      // Best-effort. A warm failure doesn't block rendering.
    });
  }, [company.id, reportId]);

  return (
    <PageContent>
      <div className="pt-4 pb-4 md:pt-6 space-y-3">
        <PageHeader compact title={reportName ?? "Report"}
          actions={<>
            <span className="hidden lg:inline text-[10px] text-muted-foreground font-mono" title={reportId}>{reportId}</span>
            {isAdmin && <Button variant="outline" size="sm" onClick={() => nav({to:"/$companySlug/reports/$reportId/edit",params:{companySlug,reportId}})}><Pencil className="size-4" /> Design</Button>}
          </>}
        />
        {/* Light-mode bubble around the viewer — see edit.tsx for the
            same pattern. Keeps the rest of the app's chrome in dark
            mode while shielding Telerik's light-only viewer from our
            Tailwind dark tokens. */}
        <div
          style={{
            position: "relative",
            height: "calc(100vh - 140px)",
            background: "#ffffff",
            color: "#000000",
            colorScheme: "light",
          }}
        >
          <TelerikReportViewer
            serviceUrl={serviceUrl}
            reportSource={{ report: `${reportId}.trdx`, parameters: {} }}
            viewerContainerStyle={{ position: "absolute", inset: 0 }}
            viewMode="INTERACTIVE"
            scaleMode="SPECIFIC"
            scale={1.0}
            parametersAreaVisible={false}
          />
        </div>
      </div>
    </PageContent>
  );
}
